<template>
  <div class="map-page">
    <div class="desktop-only">
      <NavPage v-if="!isMapFullscreen" />
      <HeaderPage v-if="!isMapFullscreen" />
    </div>
    <div class="mobile-only">
      <MobileHeader
        v-if="!isMapFullscreen && isMobile"
        :accepted-order="acceptedOrderDetails"
      />
    </div>
    <PushNotificationSetup />
    
    <!-- Interaction Prompt -->
    <div
      v-if="!userInteracted"
      class="interaction-prompt"
      @click.stop="handleFirstInteraction"
      @touchstart.stop="handleFirstInteraction"
      role="button"
      tabindex="0"
      @keydown.enter="handleFirstInteraction"
      @keydown.space="handleFirstInteraction"
    >
      <div class="prompt-content">
        <p>🔊 {{ $t("Click anywhere to enable notification sounds") }}</p>
        <small>{{ $t("You'll hear alerts for new delivery orders") }}</small>
      </div>
    </div>

    <!-- Map Container -->
    <div class="map-container" :class="{ fullscreen: isMapFullscreen }">
      <div id="map" class="leaflet-map"></div>
      
      <!-- Top Left Controls -->
      <div class="map-overlay top-left">
        <div class="status-bar">
          <div class="status-info-group">
            <div class="status-item">
              <span class="status-label">📍 Location:</span>
              <span class="status-value" v-if="currentPos">
                {{ currentPos[0].toFixed(6) }}, {{ currentPos[1].toFixed(6) }}
              </span>
              <span class="status-value" v-else>Detecting...</span>
            </div>
            <div v-if="locationAccuracy" class="status-item">
              <span class="status-label">Accuracy:</span>
              <span class="status-value">{{ locationAccuracy }}m</span>
            </div>
          </div>

          <!-- User Status Badge -->
          <div v-if="!isMapFullscreen" class="user-status-badge" :class="userStatus">
            Status: {{ userStatus === "free" ? "🟢 Active" : "🔴 Busy" }}
          </div>
        </div>
      </div>

      <!-- Top Right Controls -->
      <div class="map-overlay top-right">
        <button
          @click="refreshLocation"
          class="control-btn"
          :title="$t('Refresh my location')"
        >
          🔄
        </button>
        <button
          @click="centerMap"
          class="control-btn"
          :title="$t('Center on my location')"
        >
          📍
        </button>
        <button
          @click="toggleFullscreen"
          class="control-btn"
          :title="isMapFullscreen ? $t('Exit fullscreen') : $t('Enter fullscreen')"
        >
          {{ isMapFullscreen ? "⛶" : "⛶" }}
        </button>
      </div>
    </div>

    <!-- ✅ Order Detail Button -->
    <div v-if="activeDelivery" class="order-detail-button-container">
      <button @click="openOrderDetailModal" class="order-detail-btn">
        <span class="btn-icon">📋</span>
        <span class="btn-text">
          Order #{{ activeDelivery.orderData?.order_code || activeDelivery.orderId }}
        </span>
        <span class="btn-arrow">▶</span>
      </button>
    </div>

    <!-- Order Detail Modal -->
    <div v-if="showOrderDetailModal" class="order-detail-modal" @click.self="showOrderDetailModal = false">
      <div class="order-detail-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <h3>📋 Order Details</h3>
          <button @click="showOrderDetailModal = false" class="close-modal-btn">✕</button>
        </div>

        <!-- Order Info Section -->
        <div class="order-info-section">
          <div class="info-row">
            <span class="info-label">Order ID:</span>
            <span class="info-value">#{{ orderDetailData?.order_code || orderDetailData?.id }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Status:</span>
            <span class="info-value status-badge" :class="orderDetailData?.status">
              {{ orderDetailData?.status || 'N/A' }}
            </span>
          </div>
          
          <!-- ✅ Subtotal -->
          <div class="info-row">
            <span class="info-label">Subtotal:</span>
            <span class="info-value">{{ formatPrice(orderDetailData?.subtotal || 0) }}</span>
          </div>
          
          <!-- ✅ Delivery Fee -->
          <!-- <div class="info-row">
            <span class="info-label">Delivery Fee:</span>
            <span class="info-value">{{ formatPrice(orderDetailData?.calculated_delivery_fee || orderDetailData?.delivery_fee || 0) }}</span>
          </div> -->
          
          <!-- ✅ Tax Fee -->
          <!-- <div class="info-row">
            <span class="info-label">Tax Fee:</span>
            <span class="info-value">{{ formatPrice(orderDetailData?.calculated_tax_fee || 10) }}</span>
          </div> -->
          
          <!-- ✅ Grand Total -->
          <!-- <div class="info-row total-row">
            <span class="info-label">Grand Total:</span>
            <span class="info-value total-price">{{ formatPrice(orderDetailData?.total_price || 0) }}</span>
          </div> -->
          
          <div class="info-row">
            <span class="info-label">Created:</span>
            <span class="info-value">{{ formatDate(orderDetailData?.created_at) }}</span>
          </div>
        </div>

        <!-- Shop Information -->
        <div v-if="orderDetailData?.order_groups?.length > 0" class="shop-section">
          <h4>🏪 Shop Information</h4>
          <div v-for="(group, index) in orderDetailData.order_groups" :key="index" class="shop-info">
            <div class="shop-name">{{ group.shop?.name || `Shop #${group.shop_id}` }}</div>
            <div class="shop-phone" v-if="group.shop?.phone">📞 {{ group.shop.phone }}</div>
            <div class="shop-address" v-if="group.shop?.address">📍 {{ group.shop.address }}</div>
          </div>
        </div>

        <!-- Customer Information -->
        <div v-if="orderDetailData?.user" class="customer-section">
          <h4>👤 Customer Information</h4>
          <div class="customer-info">
            <div class="customer-name">{{ orderDetailData.user.name || 'N/A' }}</div>
            <div class="customer-phone">📞 {{ orderDetailData.user.phone || 'N/A' }}</div>
            <div class="customer-address" v-if="customerLocation">
              📍 {{ customerLocation[0].toFixed(6) }}, {{ customerLocation[1].toFixed(6) }}
            </div>
          </div>
        </div>

        <!-- Location Buttons -->
        <div class="location-btn-section">
          <button
            class="location-btn"
            @click="openMap(customerLocation ? customerLocation[0] : null, customerLocation ? customerLocation[1] : null)"
          >
            📍 Customer Location
          </button>
          <button
            class="location-btn shop"
            @click="openMap(shopLocation ? shopLocation[0] : null, shopLocation ? shopLocation[1] : null)"
          >
            🏪 Shop Location
          </button>
        </div>
        <!-- Order Items Section -->
        <div v-if="orderDetailData?.order_groups?.length > 0" class="items-section">
          <h4>🛒 Order Items</h4>
          
          <div v-for="(group, gIdx) in orderDetailData.order_groups" :key="gIdx" class="group-items">
            <div class="group-shop-name" v-if="group.shop">
              🏪 {{ group.shop.name }}
            </div>
            
            <!-- ✅ Order Items -->
            <div v-for="(item, iIdx) in group.order_items" :key="iIdx" class="item-row">
              <div class="item-info">
                <!-- ✅ Main Food -->
                <span class="item-name">{{ item.food?.name || `Item ${iIdx + 1}` }}</span>
                <span class="item-details">
                  {{ formatPrice(item.shop_price || item.price) }} × {{ item.quantity }}
                  <span class="item-total-label">= {{ formatPrice((item.shop_price || item.price) * item.quantity) }}</span>
                </span>
                
                <!-- ✅ Includes -->
                <div v-if="item.includes && item.includes.length > 0" class="includes-list">
                  <div v-for="(include, incIdx) in item.includes" :key="incIdx" class="include-item">
                    <span class="include-name">└ {{ include.include_food?.name || 'Include' }}</span>
                    <span class="include-details">
                      {{ formatPrice(include.shop_price || include.price) }} × {{ include.quantity }}
                      <span class="include-total">= {{ formatPrice((include.shop_price || include.price) * include.quantity) }}</span>
                      <span v-if="include.check_type === 'select'" class="include-badge select">select</span>
                      <span v-if="include.check_type === 'add'" class="include-badge add">add</span>
                    </span>
                  </div>
                </div>
              </div>
              
              <!-- ✅ Item Total - Main Food only -->
              <span class="item-total">
                {{ formatPrice((item.shop_price || item.price) * item.quantity) }}
              </span>
            </div>
          </div>
          
          <!-- ✅ Subtotal - Includes only -->
          <div class="items-total">
            <span class="total-label">Subtotal:</span>
            <span class="total-value">{{ formatPrice(orderDetailData?.shop_include_subtotal || 0) }}</span>
          </div>
          
          <!-- ✅ Delivery Fee -->
          <!-- <div class="items-total">
            <span class="total-label">Delivery Fee:</span>
            <span class="total-value">{{ formatPrice(orderDetailData?.shop_delivery_fee || orderDetailData?.delivery_fee || 0) }}</span>
          </div> -->
          
          <!-- ✅ Tax Fee -->
          <!-- <div class="items-total">
            <span class="total-label">Tax Fee:</span>
            <span class="total-value">{{ formatPrice(orderDetailData?.calculated_tax_fee || 10) }}</span>
          </div> -->
          
          <!-- ✅ Grand Total - Main Food + Delivery + Tax -->
          <!-- <div class="items-total grand-total">
            <span class="total-label">Grand Total:</span>
            <span class="total-value">{{ formatPrice(orderDetailData?.shop_grand_total || orderDetailData?.shop_main_total || 0) }}</span>
          </div> -->
        </div>

        <!-- Loading State -->
        <div v-if="orderDetailLoading" class="loading-state">
          <i class="ri-loader-4-line ri-spin"></i> Loading order details...
        </div>

        <!-- Error State -->
        <div v-if="orderDetailError" class="error-state">
          ⚠️ {{ orderDetailError }}
          <button @click="fetchOrderDetail" class="retry-btn">Retry</button>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button @click="showOrderDetailModal = false" class="close-btn">Close</button>
        </div>
      </div>
    </div>

    <!-- ✅ Order Received Button -->
    <div v-if="showOrderReceivedButton" class="action-button-container">
      <button
        @click="orderReceived"
        class="action-button received-button"
        :disabled="isOrderReceived"
      >
        <span v-if="isOrderReceived">
          <i class="ri-loader-4-line ri-spin me-1"></i> {{ $t("receiving...") }}
        </span>
        <span v-else> 📦 {{ $t("order received") }} </span>
      </button>
    </div>

    <!-- ✅ Order Complete Button -->
    <div v-if="showOrderCompleteButton" class="action-button-container">
      <button
        @click="completeDelivery"
        class="action-button complete-button"
        :disabled="isCompletingDelivery"
      >
        <span v-if="isCompletingDelivery">
          <i class="ri-loader-4-line ri-spin me-1"></i> {{ $t("completing...") }}
        </span>
        <span v-else> ✅ {{ $t("order complete") }} </span>
      </button>
    </div>

    <!-- ✅ Order Alert Modal -->
    <div v-if="showOrderAlert" class="order-alert-modal">
      <div class="alert-content">
        <button
          @click="closeAlert"
          class="close-alert-btn"
          style="position: absolute; top: 10px; right: 10px; background: none; border: none; font-size: 20px; cursor: pointer;"
        >
          ×
        </button>

        <h3>🛵 {{ $t("new delivery order") }}</h3>
        <p>
          {{ $t("order") }} #{{ currentOrder?.order_code || currentOrder?.id }} -
          {{ currentOrder?.user?.name || $t("customer") }}
        </p>
        <p>
          {{ $t("shop name") }} - {{ shopName }}
        </p>
        <p>{{ $t("total") }}: {{ formatPrice(currentOrderTotal) }}</p>

        <div class="alert-actions">
          <button
            @click="acceptDelivery"
            class="btn btn-primary"
            :disabled="isAcceptingDelivery"
          >
            <span v-if="isAcceptingDelivery">
              <i class="ri-loader-4-line ri-spin me-1"></i> {{ $t("accepting...") }}
            </span>
            <span v-else>
              <i class="ri-check-line me-1"></i> {{ $t("accept delivery") }}
            </span>
          </button>
          <button @click="declineDelivery" class="btn btn-outline-secondary">
            {{ $t("decline") }}
          </button>
        </div>
      </div>
    </div>

    <!-- ✅ Order Taken Modal -->
    <div v-if="showOrderTakenModal" class="order-taken-modal">
      <div class="taken-content">
        <h3>⚠️ {{ $t("order already taken") }}</h3>
        <p>{{ $t("This order was accepted by another delivery person.") }}</p>
        <button @click="showOrderTakenModal = false" class="btn btn-primary">
          {{ $t("ok") }}
        </button>
      </div>
    </div>

   
    <!-- Order Complete Confirmation Modal -->
    <div v-if="showOrderCompleteModal" class="order-complete-modal">
      <div class="complete-modal-content">
        <h3>✅ {{ $t("complete delivery") }}</h3>
        <p>{{ $t("Please confirm the order details before completing delivery") }}:</p>

        <!-- ⭐ Order Summary -->
        <div v-if="orderDetailData" class="order-summary">
          
          <!-- Order Code -->
          <div class="summary-row">
            <span class="summary-label">📋 Order ID:</span>
            <span class="summary-value">{{ orderDetailData.order_code || 'N/A' }}</span>
          </div>

          <!-- Shop Info -->
          <div v-if="orderDetailData.order_groups?.length > 0" class="shop-group">
            <div class="shop-name">
              🏪 {{ orderDetailData.order_groups[0]?.shop?.name || 'Shop' }}
            </div>
            <div class="shop-phone" v-if="orderDetailData.order_groups[0]?.shop?.phone">
              📞 {{ orderDetailData.order_groups[0].shop.phone }}
            </div>
          </div>

          <!-- Customer Info -->
          <div v-if="orderDetailData.user" class="customer-info">
            <div class="customer-name">
              👤 {{ orderDetailData.user.name || 'Customer' }}
            </div>
            <div class="customer-phone" v-if="orderDetailData.user.phone">
              📞 {{ orderDetailData.user.phone }}
            </div>
          </div>

          <!-- ✅ Order Items - SHOW CUSTOMER PRICE (WITH MARKUP) -->
          <div class="items-summary">
            <div class="items-title">🛒 Order Items (Customer Price):</div>
            
            <div 
              v-for="(group, gIdx) in orderDetailData.order_groups" 
              :key="gIdx"
              class="group-items"
            >
              <!-- ✅ Order Items - Customer Prices -->
              <div 
                v-for="(item, iIdx) in group.order_items" 
                :key="iIdx" 
                class="item-row-mini"
              >
                <!-- Main Food - Customer Price -->
                <span class="item-name-mini">{{ item.food?.name || 'Item' }}</span>
                <span class="item-total-mini">{{ formatPrice(item.customer_total || item.original_price * item.quantity || 0) }}</span>
                
                <!-- Includes -->
                <div v-if="item.includes && item.includes.length > 0" class="mini-includes">
                  <div v-for="(include, incIdx) in item.includes" :key="incIdx" class="mini-include">
                    └ {{ include.include_food?.name || 'Include' }}
                    {{ formatPrice(include.customer_price || include.original_price) }} × {{ include.quantity }}
                    <span v-if="include.check_type === 'select'" class="include-badge select">select</span>
                    <span v-if="include.check_type === 'add'" class="include-badge add">add</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ✅ Delivery Fee -->
          <div class="summary-row">
            <span class="summary-label">🚚 Delivery Fee:</span>
            <span class="summary-value">{{ formatPrice(orderDetailData?.delivery_fee || 0) }}</span>
          </div>

          <!-- ✅ Tax Fee -->
          <div class="summary-row">
            <span class="summary-label">💰 Tax Fee:</span>
            <span class="summary-value">{{ formatPrice(orderDetailData?.calculated_tax_fee || 10) }}</span>
          </div>

          <!-- ✅ Grand Total - Customer Price -->
          <div class="summary-item total">
            <span class="summary-label">💳 Total (Customer):</span>
            <!-- <span class="summary-value">{{ formatPrice(orderDetailData?.original_total_price || 0) }}</span> -->
             <span class="summary-value">{{ formatPrice( orderDetailData?.original_total_price || 0) }}</span>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="orderDetailLoading" class="loading-state">
          <i class="ri-loader-4-line ri-spin"></i> Loading order details...
        </div>

        <!-- Action Buttons -->
        <div class="complete-actions">
          <button
            @click="confirmCompleteDelivery"
            class="btn btn-primary"
            :disabled="isCompletingDelivery || orderDetailLoading"
          >
            <span v-if="isCompletingDelivery">
              <i class="ri-loader-4-line ri-spin me-1"></i> {{ $t("completing...") }}
            </span>
            <span v-else>
              ✅ {{ $t("confirm complete") }}
            </span>
          </button>
          <button
            @click="showOrderCompleteModal = false"
            class="btn btn-outline-secondary"
          >
            {{ $t("cancel") }}
          </button>
        </div>
      </div>
    </div>

    <FooterPage v-if="!isMapFullscreen" />
  </div>
</template>

<script setup>
import HeaderPage from "../header/HeaderPage.vue";
import FooterPage from "../footer/FooterPage.vue";
import MobileHeader from "../header/MobileHeader.vue";
import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import L from "leaflet";
import "leaflet-rotatedmarker";
import "leaflet/dist/leaflet.css";
import axios from "@/api";
import { useToast } from "vue-toastification";
import { getUser } from "@/utils/permission";
import NavPage from "../navigation/NavPage.vue";
import notificationMp3 from "@/assets/sounds/attention-man-deep-voice.mp3";
import PushNotificationSetup from "@/components/PushNotificationSetup.vue";
import { onMessage, messaging } from "@/services/firebase";
import {
  requestNotificationPermission,
  sendNotificationViaServiceWorker,
} from "@/utils/notification";
import { getToken } from "@/services/firebase";

// ==================== REFS ====================
const isMapFullscreen = ref(false);
const currentPos = ref(null);
const isMobile = ref(window.innerWidth < 768);
const heading = ref(0);
const lastPosition = ref(null);
const locationAccuracy = ref(null);

const acceptedOrderDetails = ref(null);
const showOrderCompleteModal = ref(false);
const showOrderTakenModal = ref(false);

// ✅ Delivery Flow States
const showOrderReceivedButton = ref(false);
const showOrderCompleteButton = ref(false);

// ✅ Order Alert
const showOrderAlert = ref(false);
const currentOrder = ref(null);
const pendingDeliveries = ref([]);
const activeDelivery = ref(null);
const deliveryStatus = ref("free");

// ✅ Location Data from order_delivery_locations
const shopLocation = ref(null);
const customerLocation = ref(null);
const shopName = ref("");

// ✅ Order Detail Modal
const showOrderDetailModal = ref(false);
const orderDetailData = ref(null);
const orderDetailLoading = ref(false);
const orderDetailError = ref(null);

// ✅ Map Instances
let map = null;
let userMarker = null;
let shopMarker = null;
let customerMarker = null;
let watchId = null;



// ✅ Sound Management

const audioPlayer = ref(null);
const userInteracted = ref(false);
const pendingNotifications = ref([]);
const soundInterval = ref(null);
const isAudioEnabled = ref(true);
const isSoundPlaying = ref(false);
const toast = useToast();

// ✅ Arrow Icon
const arrowIcon = L.icon({
  iconUrl: "/icon/navigation.png",
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

// ==================== COMPUTED ====================
const userStatus = computed(() => {
  if (activeDelivery.value) {
    if (showOrderReceivedButton.value) return "picking_up";
    if (showOrderCompleteButton.value) return "delivering";
    return "busy";
  }
  return "free";
});

const currentOrderTotal = computed(() => {
  if (!currentOrder.value) return 0;
  return parseFloat(currentOrder.value.original_total_price || 0);
});

const customerName = computed(() => {
  if (!currentOrder.value?.user) return "Customer";
  return currentOrder.value.user.name || "Customer";
});

const customerPhone = computed(() => {
  if (!currentOrder.value?.user) return "N/A";
  return currentOrder.value.user.phone || "N/A";
});

// ==================== ORDER DETAIL FUNCTIONS ====================

/**
 * ✅ Order Detail Modal ကိုဖွင့်မယ်
 */
const openOrderDetailModal = () => {
  showOrderDetailModal.value = true;
  fetchOrderDetail();
};

/**
 * ✅ Order Detail ကို Fetch လုပ်မယ်
 */

const fetchOrderDetail = async () => {
  if (!activeDelivery.value) {
    toast.error("No active delivery found");
    return;
  }

  const orderId = activeDelivery.value.orderId;
  
  orderDetailLoading.value = true;
  orderDetailError.value = null;
  
  try {
    console.log("📋 Fetching order detail for:", orderId);
    const response = await axios.get(`/delivery-order-detail/${orderId}`);
    
    orderDetailData.value = response.data;
    console.log("✅ Order detail fetched:", orderDetailData.value);
    
    // ✅ Update location data from order detail
    if (orderDetailData.value?.order_groups?.length > 0) {
      const group = orderDetailData.value.order_groups[0];
      if (group.shop) {
        shopName.value = group.shop.name || "Shop";
      }
      if (orderDetailData.value.delivery_locations?.length > 0) {
        const loc = orderDetailData.value.delivery_locations[0];
        shopLocation.value = [
          parseFloat(loc.shop_latitude),
          parseFloat(loc.shop_longitude)
        ];
        customerLocation.value = [
          parseFloat(loc.customer_latitude),
          parseFloat(loc.customer_longitude)
        ];
      }
    }
    
    // ✅ If Order Complete Modal is waiting for data, show it
    if (showOrderCompleteModal.value) {
      // Data is loaded, modal will show updated data
    }
    
  } catch (error) {
    console.error("❌ Failed to fetch order detail:", error);
    orderDetailError.value = error.response?.data?.error || error.message || "Failed to load order details";
    toast.error(orderDetailError.value);
  } finally {
    orderDetailLoading.value = false;
  }
};
/**
 * ✅ Google Map ကိုဖွင့်မယ်
 */
const openMap = (lat, lng) => {
  if (!lat || !lng) {
    toast.error("Location not available");
    return;
  }

  const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  window.open(url, "_blank");
};

const calculateIncludesTotal = (includes) => {
  if (!includes || includes.length === 0) return 0;
  
  let total = 0;
  includes.forEach(include => {
    const price = parseFloat(include.price) || 0;
    const quantity = parseInt(include.quantity) || 1;
    total += price * quantity;
  });
  
  return total;
};

/**
 * ✅ Format Date
 */
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString();
};

// ==================== LOCATION FUNCTIONS ====================

const initMap = () => {
  if (!map) {
    map = L.map("map", {
      zoomControl: false,
      attributionControl: false,
      tap: !L.Browser.mobile,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      detectRetina: true,
    }).addTo(map);

    L.control.zoom({ position: "bottomright" }).addTo(map);

    const initialView = [20.4478, 99.8801];
    map.setView(initialView, 13);

    initUserLocation();
  }
};

const initUserLocation = () => {
  if (!navigator.geolocation) {
    toast.error("Your browser doesn't support location services");
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const accuracy = pos.coords.accuracy;

      currentPos.value = [lat, lng];
      locationAccuracy.value = Math.round(accuracy);

      if (userMarker) {
        userMarker.setLatLng(currentPos.value);
      } else {
        userMarker = L.marker(currentPos.value, {
          icon: arrowIcon,
          rotationAngle: heading.value,
          zIndexOffset: 1000,
        }).addTo(map);
      }

      centerMap();
      startWatchingPosition();
      toast.success(`📍 Location detected! Accuracy: ${Math.round(accuracy)}m`);
    },
    (err) => {
      console.error("📍 Geolocation failed:", err);
      toast.error("Could not detect your location. Using default location.");
      currentPos.value = [20.4478, 99.8801];
      if (userMarker) {
        userMarker.setLatLng(currentPos.value);
      } else if (map) {
        userMarker = L.marker(currentPos.value, {
          icon: arrowIcon,
          zIndexOffset: 1000,
        }).addTo(map);
      }
      centerMap();
    },
    { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
  );
};

const startWatchingPosition = () => {
  watchId = navigator.geolocation.watchPosition(
    handlePositionUpdate,
    (error) => {
      console.error("📍 Continuous location error:", error);
    },
    { enableHighAccuracy: true, maximumAge: 30000, timeout: 15000 }
  );
};

const handlePositionUpdate = (pos) => {
  const newPos = [pos.coords.latitude, pos.coords.longitude];
  const newHeading = pos.coords.heading;
  const accuracy = pos.coords.accuracy;

  locationAccuracy.value = Math.round(accuracy);

  if (lastPosition.value) {
    const distance = L.latLng(lastPosition.value.lat, lastPosition.value.lng)
      .distanceTo(L.latLng(newPos[0], newPos[1]));
    if (distance > 10 || accuracy < pos.coords.accuracy / 2) {
      updatePosition(newPos, newHeading);
    }
  } else {
    updatePosition(newPos, newHeading);
  }
};

const updatePosition = (newPos, newHeading) => {
  if (newHeading) heading.value = newHeading;

  if (userMarker) {
    userMarker.setLatLng(newPos);
    userMarker.setRotationAngle(heading.value);
  }

  currentPos.value = newPos;
  lastPosition.value = { lat: newPos[0], lng: newPos[1], timestamp: Date.now() };
};

const centerMap = () => {
  if (map && currentPos.value) {
    map.setView(currentPos.value, 15, { animate: true, duration: 1 });
  }
};

const refreshLocation = () => {
  if (watchId) {
    navigator.geolocation.clearWatch(watchId);
    watchId = null;
  }
  if (userMarker) {
    map.removeLayer(userMarker);
    userMarker = null;
  }
  initUserLocation();
  toast.info("🔄 Refreshing your location...");
};

const toggleFullscreen = () => {
  isMapFullscreen.value = !isMapFullscreen.value;
  setTimeout(() => map.invalidateSize(), 300);
};

// ==================== ORDER FUNCTIONS ====================

/**
 * ✅ Order တက်လာတဲ့အခါ Delivery Location တွေကို API ကနေယူမယ်
 */
const fetchDeliveryLocations = async (orderId) => {
  try {
    console.log("📍 Fetching delivery locations for order:", orderId);
    const response = await axios.get(`/orders/${orderId}/delivery-locations`);
    
    if (response.data.success && response.data.locations.length > 0) {
      const location = response.data.locations[0];
      
      shopLocation.value = [
        parseFloat(location.shop_latitude),
        parseFloat(location.shop_longitude)
      ];
      
      customerLocation.value = [
        parseFloat(location.customer_latitude),
        parseFloat(location.customer_longitude)
      ];
      
      shopName.value = location.shop_name || "Shop";
      
      console.log("✅ Shop Location:", shopLocation.value);
      console.log("✅ Customer Location:", customerLocation.value);
      
      return true;
    }
    return false;
  } catch (error) {
    console.error("❌ Failed to fetch delivery locations:", error);
    return false;
  }
};

/**
 * ✅ Order Event Handler - Order တက်လာတဲ့အခါ
 */
const handleOrderEvent = async (data) => {
  console.log("📦 Order event received:", data);

  if (!data || !data.order) {
    console.error("Invalid order event data:", data);
    return;
  }

  const orderData = data.order;
  const user = await getUser();

  // ❌ Busy ဖြစ်နေရင် Skip
  if (deliveryStatus.value !== "free") {
    console.log("⏭️ Busy, skipping order:", orderData.id);
    return;
  }

  // ✅ Shop က Accept လုပ်ပြီး Delivery အတွက် အဆင်သင့်ဖြစ်နေတယ်
  if (
    (data.event_type === "order.accepted" && orderData.status === "accepted") ||
    (data.event_type === "order.assigned" && orderData.status === "assigned")
  ) {
    console.log("🛵 New order available:", orderData.order_code);

    // ✅ Delivery Locations ကိုယူမယ်
    const locationsFetched = await fetchDeliveryLocations(orderData.id);
    
    if (!locationsFetched) {
      toast.error("Could not fetch delivery locations");
      return;
    }

    // ✅ Pending Deliveries ထဲထည့်မယ်
    const existingIndex = pendingDeliveries.value.findIndex(
      (d) => d.order.id === orderData.id
    );

    if (existingIndex === -1) {
      pendingDeliveries.value.push({
        order: orderData,
        timestamp: Date.now(),
      });

      pendingDeliveries.value.sort((a, b) => b.timestamp - a.timestamp);

      // ✅ Alert ပြမယ်
      if (deliveryStatus.value === "free" && !showOrderAlert.value) {
        const nextDelivery = pendingDeliveries.value[0];
        currentOrder.value = nextDelivery.order;
        showOrderAlert.value = true;
        playNotificationSound();
      }
    }
    return;
  }

  // ✅ Admin က Assign လုပ်တယ်
  if (orderData.delivery_id === user.id && orderData.status === "onway") {
    console.log("📋 Admin assigned order:", orderData);
    await handleAssignedDelivery(orderData);
    return;
  }

  // ✅ တခြား Delivery Person ကို Assign လုပ်တယ်
  if (orderData.delivery_id && orderData.delivery_id !== user.id) {
    console.log("Order assigned to another delivery person");
    removeFromPendingDeliveries(orderData.id);
    return;
  }
};

/**
 * ✅ Assigned Delivery ကို ကိုင်တွယ်မယ်
 */
const handleAssignedDelivery = async (orderData) => {
  // ✅ Delivery Locations ကိုယူမယ်
  const locationsFetched = await fetchDeliveryLocations(orderData.id);
  
  if (!locationsFetched) {
    toast.error("Could not fetch delivery locations");
    return;
  }

  // ✅ Active Delivery ကို Set လုပ်မယ်
  activeDelivery.value = {
    orderId: orderData.id,
    orderData: orderData,
    timestamp: Date.now(),
  };

  // ✅ Shop Marker ပြမယ်
  showShopMarker();

  // ✅ Status ကို "busy" ပြောင်းမယ်
  deliveryStatus.value = "busy";

  // ✅ Order Received Button ပြမယ်
  showOrderReceivedButton.value = true;

  toast.success(`📦 Delivery assigned: Order #${orderData.order_code}`);
};

/**
 * ✅ Shop Marker ပြမယ်
 */
const showShopMarker = () => {
  if (!shopLocation.value || !map) return;

  if (shopMarker) {
    map.removeLayer(shopMarker);
    shopMarker = null;
  }

  shopMarker = L.marker(shopLocation.value, {
    icon: L.divIcon({
      html: `<div style="
        background: white;
        border: 3px solid #4CAF50;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      ">🏪</div>`,
      className: "shop-marker",
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    }),
  }).addTo(map);

  shopMarker.bindPopup(`
    <div style="min-width: 150px;">
      <div style="font-weight: bold;">🏪 ${shopName.value}</div>
      <div style="font-size: 12px; color: #666;">Pickup location</div>
    </div>
  `);

  map.setView(shopLocation.value, 15);
};

/**
 * ✅ Customer Marker ပြမယ်
 */
const showCustomerMarker = () => {
  if (!customerLocation.value || !map) return;

  if (customerMarker) {
    map.removeLayer(customerMarker);
    customerMarker = null;
  }

  customerMarker = L.marker(customerLocation.value, {
    icon: L.divIcon({
      html: `<div style="
        background: white;
        border: 3px solid #2196F3;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      ">👤</div>`,
      className: "customer-marker",
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    }),
  }).addTo(map);

  customerMarker.bindPopup(`
    <div style="min-width: 150px;">
      <div style="font-weight: bold;">👤 ${customerName.value}</div>
      <div style="font-size: 12px;">📞 ${customerPhone.value}</div>
      <div style="font-size: 12px; color: #666;">Drop-off location</div>
    </div>
  `);

  map.setView(customerLocation.value, 15);
};

/**
 * ✅ Accept Delivery
 */
const acceptDelivery = async () => {
  isAcceptingDelivery.value = true;
  
  try {
    const user = await getUser();

    const isAdminAssigned =
      currentOrder.value.delivery_id != null &&
      currentOrder.value.delivery_id === user.id;

    let acceptResponse;

    if (isAdminAssigned) {
      acceptResponse = await axios.post(`/orders/${currentOrder.value.id}/deli_accept`, {
        delivery_person_id: user.id,
        status: "onway",
      });
    } else {
      try {
        await axios.post(`/orders/${currentOrder.value.id}/assign`, {
          delivery_person_id: user.id,
        });
        acceptResponse = await axios.post(`/orders/${currentOrder.value.id}/deli_accept`, {
          delivery_person_id: user.id,
          status: "onway",
        });
      } catch (assignError) {
        if (assignError.response?.status === 409) {
          acceptResponse = await axios.post(`/orders/${currentOrder.value.id}/deli_accept`, {
            delivery_person_id: user.id,
            status: "onway",
          });
        } else {
          throw assignError;
        }
      }
    }

    const acceptedOrder = acceptResponse.data.order || acceptResponse.data;
    
    pendingDeliveries.value = [];
    showOrderAlert.value = false;

    activeDelivery.value = {
      orderId: acceptedOrder.id,
      orderData: acceptedOrder,
    };

    await fetchDeliveryLocations(acceptedOrder.id);
    showShopMarker();

    deliveryStatus.value = "busy";
    showOrderReceivedButton.value = true;

    toast.success("✅ Delivery accepted!");
  } catch (error) {
    console.error("Accept delivery error:", error);

    if (error.response?.status === 409 || error.response?.status === 403) {
      toast.error("This order was already taken by another delivery person");
      showOrderAlert.value = false;
      showOrderTakenModal.value = true;
      removeFromPendingDeliveries(currentOrder.value.id);
    } else {
      toast.error("Failed to accept delivery: " + (error.response?.data?.message || error.message));
    }
  } finally {
    isAcceptingDelivery.value = false;
  }
};

/**
 * ✅ Order Received (ဆိုင်ကနေ ပစ္စည်းကောက်ပြီး)
 */
const orderReceived = async () => {
  if (!activeDelivery.value) {
    toast.error("No active delivery found");
    return;
  }

  isOrderReceived.value = true;

  try {
    await axios.post(`/orders/${activeDelivery.value.orderId}/picked-up`, {
      status: "picked_up",
    });

    toast.success("📦 Order received from shop!");

    showOrderReceivedButton.value = false;
    showCustomerMarker();
    showOrderCompleteButton.value = true;

  } catch (error) {
    console.error("Order received error:", error);
    toast.error("Failed to update order status: " + (error.response?.data?.error || error.message));
    showOrderReceivedButton.value = true;
  } finally {
    isOrderReceived.value = false;
  }
};

/**
 * ✅ Complete Delivery
 */
const completeDelivery = () => {
  // ✅ MUST use orderDetailData
  if (!orderDetailData.value) {
    toast.info("Loading order details...");
    // Fetch order detail first
    fetchOrderDetail();
    // Show modal after data is loaded
    const checkData = setInterval(() => {
      if (orderDetailData.value) {
        clearInterval(checkData);
        showOrderCompleteModal.value = true;
      }
    }, 500);
    return;
  }

  showOrderCompleteModal.value = true;
};

/**
 * ✅ Confirm Complete Delivery
 */
const confirmCompleteDelivery = async () => {
  if (!activeDelivery.value) {
    toast.error("No active delivery found");
    return;
  }

  isCompletingDelivery.value = true;

  try {
    await axios.post(`/orders/${activeDelivery.value.orderId}/completed`, {
      status: "completed",
    });

    toast.success("✅ Delivery completed successfully!");

    showOrderCompleteModal.value = false;
    showOrderCompleteButton.value = false;
    activeDelivery.value = null;
    currentOrder.value = null;
    orderDetailData.value = null; // ✅ Clear order detail data
    deliveryStatus.value = "free";
    shopLocation.value = null;
    customerLocation.value = null;

    if (shopMarker) {
      map.removeLayer(shopMarker);
      shopMarker = null;
    }
    if (customerMarker) {
      map.removeLayer(customerMarker);
      customerMarker = null;
    }

    // Refresh page to reset state
    setTimeout(() => {
      window.location.href = window.location.origin + window.location.pathname + "?refresh=" + Date.now();
    }, 1500);

  } catch (error) {
    console.error("Order complete error:", error);
    toast.error("Failed to complete delivery: " + (error.message || "Unknown error"));
  } finally {
    isCompletingDelivery.value = false;
  }
};

/**
 * ✅ Decline Delivery
 */
const declineDelivery = () => {
  showOrderAlert.value = false;
  const declinedIndex = pendingDeliveries.value.findIndex(
    (d) => d.order.id === currentOrder.value.id
  );
  if (declinedIndex !== -1) {
    pendingDeliveries.value.splice(declinedIndex, 1);
  }
  currentOrder.value = null;
  toast.info("Delivery declined");

  if (pendingDeliveries.value.length > 0) {
    setTimeout(showNextDeliveryAlert, 1000);
  }
};

/**
 * ✅ Remove from Pending Deliveries
 */
const removeFromPendingDeliveries = (orderId) => {
  const index = pendingDeliveries.value.findIndex((d) => d.order.id === orderId);
  if (index !== -1) {
    pendingDeliveries.value.splice(index, 1);
    if (showOrderAlert.value && currentOrder.value?.id === orderId) {
      showOrderAlert.value = false;
      showOrderTakenModal.value = true;
      if (pendingDeliveries.value.length > 0) {
        setTimeout(showNextDeliveryAlert, 1000);
      }
    }
  }
};

/**
 * ✅ Show Next Delivery Alert
 */
const showNextDeliveryAlert = () => {
  if (showOrderAlert.value || activeDelivery.value || pendingDeliveries.value.length === 0) {
    return;
  }

  const nextDelivery = pendingDeliveries.value[0];
  currentOrder.value = nextDelivery.order;
  showOrderAlert.value = true;
  playNotificationSound();
};

// ==================== SOUND MANAGEMENT ====================

const handleFirstInteraction = (event) => {
  // Prevent all default behavior
  if (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  if (!userInteracted.value) {
    userInteracted.value = true;
    console.log("👆 User interacted, audio can now play");

    // Play any pending notifications
    playPendingNotifications();

    // Remove event listeners
    document.removeEventListener("click", handleFirstInteraction);
    document.removeEventListener("touchstart", handleFirstInteraction);
    document.removeEventListener("keydown", handleFirstInteraction);
  }
};

const initInteractionListeners = () => {
  document.addEventListener("click", handleFirstInteraction);
  document.addEventListener("touchstart", handleFirstInteraction);
  document.addEventListener("keydown", handleFirstInteraction);
};

const playNotificationSound = async () => {
  // ✅ ဒီအတိုင်းထားပါ
  if (!isAudioEnabled.value || !audioPlayer.value || !userInteracted.value) {
    console.log("🔊 Sound not playing - checks:", {
      isAudioEnabled: isAudioEnabled.value,
      audioPlayer: !!audioPlayer.value,
      userInteracted: userInteracted.value
    });
    return;
  }

  stopNotificationSound();

  try {
    audioPlayer.value.currentTime = 0;
    audioPlayer.value.loop = false;

    // Play the sound
    await audioPlayer.value.play();
    isSoundPlaying.value = true;
    console.log("🔊 Notification sound started");

    // Loop 3 times
    let playCount = 0;
    const maxPlays = 3;
    const soundDuration = 3000;

    soundInterval.value = setInterval(() => {
      playCount++;
      if (playCount >= maxPlays) {
        stopNotificationSound();
        return;
      }
      if (audioPlayer.value && audioPlayer.value.ended) {
        audioPlayer.value.currentTime = 0;
        audioPlayer.value.play().catch(err => console.error("🔊 Play error:", err));
      }
    }, soundDuration);

    // Stop after 10 seconds
    setTimeout(() => {
      stopNotificationSound();
    }, 10000);

  } catch (error) {
    console.error("🔊 Failed to play notification sound:", error);
    if (error.name === "NotAllowedError") {
      toast.info("👆 Please click anywhere to enable notification sounds");
      isAudioEnabled.value = false;
    }
  }
};

// ==================== CHECK FOR CURRENT ORDER ON LOAD ====================

// const checkCurrentOrderOnLoad = async () => {
//   try {
//     console.log("🔍 Checking for current active order...");

//     const response = await axios.get('/delivery/current-order');

//     console.log("📦 Current order response:", response.data);

//     if (response.data.success && response.data.has_active_order) {
//       const orderData = response.data.order;
//       const uiState = response.data.ui_state;

//       console.log("✅ Found active order:", {
//         order_id: orderData.id,
//         status: orderData.status,
//         ui_state: uiState
//       });

//       // ✅ Set order data
//       currentOrder.value = orderData;

//       // ✅ Set active delivery
//       activeDelivery.value = {
//         orderId: orderData.id,
//         orderData: orderData,
//         timestamp: Date.now(),
//       };

//       // ✅ Set delivery status
//       deliveryStatus.value = uiState.delivery_status || 'busy';

//       // ✅ Set location data from delivery_locations
//       if (orderData.delivery_locations && orderData.delivery_locations.length > 0) {
//         const location = orderData.delivery_locations[0];
//         shopLocation.value = [
//           parseFloat(location.shop_latitude),
//           parseFloat(location.shop_longitude)
//         ];
//         customerLocation.value = [
//           parseFloat(location.customer_latitude),
//           parseFloat(location.customer_longitude)
//         ];
//         shopName.value = location.shop_name || "Shop";
//       }

//       // ✅ Show/hide buttons based on status
//       showOrderReceivedButton.value = uiState.show_order_received_button || false;
//       showOrderCompleteButton.value = uiState.show_order_complete_button || false;

//       // ✅ Set accepted order details for header
//       acceptedOrderDetails.value = {
//         shopName: shopName.value,
//         shopPhone: orderData.order_groups?.[0]?.shop?.phone || "N/A",
//         customerName: orderData.user?.name || "Customer",
//         customerPhone: orderData.user?.phone || "N/A",
//         orderCode: orderData.order_code || `#${orderData.id}`,
//         subtotal: formatPrice(orderData.total_price || 0),
//         isCustomerStage: orderData.status === 'picked_up',
//       };

//       // ✅ Show markers on map
//       if (shopLocation.value) {
//         showShopMarker();
//       }

//       if (showOrderCompleteButton.value && customerLocation.value) {
//         showCustomerMarker();
//       }

//       toast.success(`🔄 Restored order #${orderData.order_code}`);
//       return true;
//     } else {
//       console.log("ℹ️ No active order found");
//       return false;
//     }
//   } catch (error) {
//     console.error("❌ Failed to check current order:", error);
//     return false;
//   }
// };

const checkCurrentOrderOnLoad = async () => {
  try {
    console.log("🔍 Checking for current order...");

    const response = await axios.get('/delivery/current-order');

    console.log("📦 Current order response:", response.data);

    if (!response.data.success || !response.data.has_active_order) {
      console.log("ℹ️ No current order found");
      return false;
    }

    const orderData = response.data.order;
    const uiState = response.data.ui_state || {};

    console.log("📦 Restoring order:", {
      order_id: orderData.id,
      status: orderData.status,
      ui_state: uiState,
    });

    /*
    |--------------------------------------------------------------------------
    | IMPORTANT
    |
    | assigned = NOT ACCEPTED
    | onway = ACCEPTED
    | picked_up = ACCEPTED + PICKED UP
    |--------------------------------------------------------------------------
    */

    currentOrder.value = orderData;

    /*
    |--------------------------------------------------------------------------
    | 1. ASSIGNED
    |
    | Admin assigned this delivery person,
    | but delivery has NOT accepted yet.
    |--------------------------------------------------------------------------
    */

    if (orderData.status === 'assigned') {

      console.log(
        "📋 Order is assigned but NOT accepted yet"
      );

      // IMPORTANT: no active delivery
      activeDelivery.value = null;

      // Delivery is still free
      deliveryStatus.value = 'free';

      // Show Accept Modal
      showOrderAlert.value = true;

      // DO NOT show Order Received
      showOrderReceivedButton.value = false;

      // DO NOT show Complete
      showOrderCompleteButton.value = false;

      // Load locations
      await fetchDeliveryLocations(orderData.id);

      // Make sure this order is in pending list
      const existingIndex = pendingDeliveries.value.findIndex(
        d => d.order.id === orderData.id
      );

      if (existingIndex === -1) {
        pendingDeliveries.value.push({
          order: orderData,
          timestamp: Date.now(),
          assignedBy: 'admin',
          delivery_id: orderData.delivery_id,
        });
      }

      pendingDeliveries.value.sort(
        (a, b) => b.timestamp - a.timestamp
      );

      if (shopLocation.value) {
        showShopMarker();
      }

      playNotificationSound();

      toast.info(
        `📋 Order #${orderData.order_code} is waiting for your acceptance`
      );

      return true;
    }

    /*
    |--------------------------------------------------------------------------
    | 2. ACCEPTED / ONWAY
    |
    | Delivery already pressed ACCEPT.
    |--------------------------------------------------------------------------
    */

    if (orderData.status === 'onway') {

      console.log(
        "✅ Order was already accepted - restoring active delivery"
      );

      activeDelivery.value = {
        orderId: orderData.id,
        orderData: orderData,
        timestamp: Date.now(),
      };

      deliveryStatus.value = 'busy';

      showOrderAlert.value = false;

      showOrderReceivedButton.value = true;

      showOrderCompleteButton.value = false;

      await fetchDeliveryLocations(orderData.id);

      acceptedOrderDetails.value = {
        shopName: shopName.value,
        shopPhone:
          orderData.order_groups?.[0]?.shop?.phone || "N/A",

        customerName:
          orderData.user?.name || "Customer",

        customerPhone:
          orderData.user?.phone || "N/A",

        orderCode:
          orderData.order_code || `#${orderData.id}`,

        subtotal:
          formatPrice(orderData.total_price || 0),

        isCustomerStage: false,
      };

      if (shopLocation.value) {
        showShopMarker();
      }

      toast.success(
        `🔄 Restored accepted order #${orderData.order_code}`
      );

      return true;
    }

    /*
    |--------------------------------------------------------------------------
    | 3. PICKED UP
    |--------------------------------------------------------------------------
    */

    if (orderData.status === 'picked_up') {

      console.log(
        "📦 Order was already picked up - restoring delivery"
      );

      activeDelivery.value = {
        orderId: orderData.id,
        orderData: orderData,
        timestamp: Date.now(),
      };

      deliveryStatus.value = 'busy';

      showOrderAlert.value = false;

      showOrderReceivedButton.value = false;

      showOrderCompleteButton.value = true;

      await fetchDeliveryLocations(orderData.id);

      acceptedOrderDetails.value = {
        shopName: shopName.value,
        shopPhone:
          orderData.order_groups?.[0]?.shop?.phone || "N/A",

        customerName:
          orderData.user?.name || "Customer",

        customerPhone:
          orderData.user?.phone || "N/A",

        orderCode:
          orderData.order_code || `#${orderData.id}`,

        subtotal:
          formatPrice(orderData.total_price || 0),

        isCustomerStage: true,
      };

      if (shopLocation.value) {
        showShopMarker();
      }

      if (customerLocation.value) {
        showCustomerMarker();
      }

      toast.success(
        `🔄 Restored delivery #${orderData.order_code}`
      );

      return true;
    }

    console.warn(
      "⚠️ Unknown order status:",
      orderData.status
    );

    return false;

  } catch (error) {

    console.error(
      "❌ Failed to check current order:",
      error
    );

    return false;
  }
};

const stopNotificationSound = () => {
  if (soundInterval.value) {
    clearInterval(soundInterval.value);
    soundInterval.value = null;
  }
  if (audioPlayer.value) {
    audioPlayer.value.pause();
    audioPlayer.value.currentTime = 0;
  }
  isSoundPlaying.value = false;
};

const playPendingNotifications = () => {
  if (pendingNotifications.value.length > 0) {
    pendingNotifications.value.forEach((_, index) => {
      setTimeout(() => {
        if (audioPlayer.value) {
          audioPlayer.value.currentTime = 0;
          audioPlayer.value.play().catch(console.error);
        }
      }, index * 1000);
    });
    pendingNotifications.value = [];
  }
};

// ==================== ECHO LISTENERS (REALTIME) ====================

const setupEchoListeners = async () => {
  if (!window.Echo) {
    console.error("Echo is not available");
    return;
  }

  try {
    const user = await getUser();
    console.log("Setting up Echo listeners:", user.id);

    // ✅ Leave previous channels to avoid duplicates
    window.Echo.leave("orders.delivery");
    window.Echo.leave(`delivery.${user.id}`);

    // ✅ 1. Public Channel - All delivery persons
    window.Echo.private("orders.delivery")
      .listen(".order.accepted", (data) => {
        console.log("📦 New order available:", data);
        if (!data.is_manual_assignment) {
          handleOrderEvent(data);
        }
      })
      .listen(".order.placed", (data) => {
        console.log("📦 Order placed:", data);
      })
      .listen(".order.assigned", (data) => {
        console.log("📦 Order assigned:", data);
        handleOrderEvent(data);
      })
      .listen(".order.accepted.by.others", (data) => {
        console.log("📦 Order accepted by others:", data);
        handleOrderAcceptedByOthers(data);
      });

    // ✅ 2. Personal Channel - Specific delivery person
    const personalChannel = window.Echo.private(`delivery.${user.id}`);

    personalChannel.subscribed(() => {
      console.log(`✅ Successfully subscribed to delivery.${user.id}`);
    });

    personalChannel
      .listen(".order.assigned", (data) => {
        console.log("📦 Admin assigned order to me:", data);
        handleAdminAssignedOrder(data);
      })
      .listen(".order.updated", (data) => {
        console.log("📦 Order updated:", data);
        handleOrderEvent(data);
      })
      .listen(".order.taken", (data) => {
        console.log("📦 Order taken by someone else:", data);
        handleOrderTakenByOthers(data);
      });

    console.log("✅ Echo listeners setup successfully");
  } catch (error) {
    console.error("❌ Failed to setup Echo listeners:", error);
    toast.error("Failed to connect to real-time updates");
  }
};

const handleAdminAssignedOrder = (data) => {
  console.log("🚚 Admin assigned order:", data);
  showOrderAlert.value = false;
  currentOrder.value = data.order;

  const existingIndex = pendingDeliveries.value.findIndex(
    (d) => d.order.id === data.order.id
  );

  if (existingIndex === -1) {
    fetchDeliveryLocations(data.order.id).then(() => {
      pendingDeliveries.value.push({
        order: data.order,
        timestamp: Date.now(),
        assignedBy: "admin",
        delivery_id: data.order.delivery_id,
      });

      pendingDeliveries.value.sort((a, b) => b.timestamp - a.timestamp);

      if (pendingDeliveries.value.length > 0 && !activeDelivery.value) {
        const nextDelivery = pendingDeliveries.value[0];
        currentOrder.value = nextDelivery.order;
        showOrderAlert.value = true;
        playNotificationSound();
      }
    });
  } else {
    if (!activeDelivery.value && !showOrderAlert.value) {
      showOrderAlert.value = true;
      playNotificationSound();
    }
  }

  toast.info(`New delivery order #${data.order.order_code} assigned to you by admin`);
};

const handleOrderAcceptedByOthers = (data) => {
  if (!data || !data.order_id) return;
  console.log("🚫 Order accepted by another:", data);
  removeFromPendingDeliveries(data.order_id);
};

const handleOrderTakenByOthers = (data) => {
  if (!data || !data.order_id) return;
  console.log("🚫 Order taken by another:", data);
  removeFromPendingDeliveries(data.order_id);
};

// ==================== PUSH NOTIFICATIONS ====================

const listenForPushMessages = () => {
  if ("Notification" in window && Notification.permission === "granted") {
    if (window._firebaseMessageListener) {
      window._firebaseMessageListener();
    }

    window._firebaseMessageListener = onMessage(messaging, (payload) => {
      console.log("🔥 Foreground message:", payload);
      const data = payload.data || payload.notification?.data || {};

      if (data.type === "new_order" || data.order_id) {
        toast.info(`🛵 New Order #${data.order_code || data.order_id}`, {
          timeout: 10000,
          closeOnClick: true,
        });
      }

      if (data.type === "order_accepted" || data.event_type === "order.accepted") {
        handlePushOrderNotification(data);
      } else if (data.type === "order_assigned" || data.event_type === "order.assigned") {
        handlePushOrderAssigned(data);
      } else if (data.type === "new_order") {
        const orderEvent = {
          event_type: "order.accepted",
          order: {
            id: data.order_id,
            order_code: data.order_code,
            status: "accepted",
            ...data,
          },
        };
        handleOrderEvent(orderEvent);
      }
    });
  }
};

const handlePushOrderNotification = (data) => {
  console.log("Push notification - Order accepted:", data);
  if (data.order) {
    handleOrderEvent({ event_type: "order.accepted", order: data.order });
  }
};

const handlePushOrderAssigned = (data) => {
  console.log("Push notification - Order assigned:", data);
  if (data.order) {
    handleAssignedDelivery(data.order);
  }
};

// ==================== UTILITY FUNCTIONS ====================

const formatPrice = (price) => Number(price || 0).toFixed(2);

const closeAlert = () => {
  showOrderAlert.value = false;
};

// ==================== REFS FOR BUTTONS ====================

const isAcceptingDelivery = ref(false);
const isOrderReceived = ref(false);
const isCompletingDelivery = ref(false);

// ==================== CHECK FOR PENDING ORDERS ON LOAD ====================

const checkForPendingOrdersOnLoad = async () => {
  try {
    if (activeDelivery.value) {
      console.log("User is busy - skipping pending orders check");
      return;
    }

    console.log("🔍 Checking for pending orders on page load...");
    const response = await axios.get("/delivery/last-pending-order");

    if (response.data.orders && response.data.orders.length > 0) {
      for (const item of response.data.orders) {
        const order = item.order;
        const existingIndex = pendingDeliveries.value.findIndex(
          (d) => d.order.id === order.id
        );

        if (existingIndex === -1) {
          await fetchDeliveryLocations(order.id);
          pendingDeliveries.value.push({
            order: order,
            timestamp: Date.now(),
            type: item.type,
            delivery_id: order.delivery_id,
          });
          console.log(`📦 Added order #${order.order_code} to pending`);
        }
      }

      pendingDeliveries.value.sort((a, b) => b.timestamp - a.timestamp);

      if (!showOrderAlert.value && pendingDeliveries.value.length > 0) {
        const latestOrder = pendingDeliveries.value[0];
        currentOrder.value = latestOrder.order;
        if (latestOrder.type === "admin_assigned") {
          currentOrder.value.delivery_id = latestOrder.delivery_id;
        }
        showOrderAlert.value = true;
        playNotificationSound();
      }
    }
  } catch (error) {
    console.error("Failed to check pending orders:", error);
  }
};

// ==================== WATCH ====================

// ✅ Order Detail Modal ကိုဖွင့်တဲ့အခါ Order Detail ကို Fetch လုပ်မယ်
watch(showOrderDetailModal, (newVal) => {
  if (newVal && activeDelivery.value) {
    fetchOrderDetail();
  }
});

// ==================== LIFECYCLE HOOKS ====================

onMounted(async () => {
  // ✅ Map
  initMap();
  window.addEventListener("resize", () => {
    isMobile.value = window.innerWidth < 768;
    if (map) setTimeout(() => map.invalidateSize(), 100);
  });

  // ✅ Audio
  audioPlayer.value = new Audio(notificationMp3);
  audioPlayer.value.preload = "auto";
  audioPlayer.value.volume = 0.7;
  document.addEventListener("click", handleFirstInteraction);
  document.addEventListener("touchstart", handleFirstInteraction);

  initInteractionListeners();

  // ✅ Echo (Realtime)
  await setupEchoListeners();

  // ✅ Push Notifications
  listenForPushMessages();
  await requestNotificationPermission();

  // ✅ Notification Permission
  if (Notification.permission === "granted") {
    const token = await getToken(messaging);
    console.log("✅ FCM Token exists:", !!token);
  } else {
    await Notification.requestPermission();
  }

  // ✅ Check for current active order (NEW - replaces localStorage)
  await checkCurrentOrderOnLoad();

  // ✅ Check for pending orders (if no active order)
  if (!activeDelivery.value) {
    setTimeout(() => {
      checkForPendingOrdersOnLoad();
    }, 1000);
  }
});

onBeforeUnmount(async () => {
  stopNotificationSound();
  if (watchId) navigator.geolocation.clearWatch(watchId);
  if (window.Echo) {
    const user = await getUser();
    window.Echo.leave(`delivery.${user.id}`);
  }
  if (map) {
    map.remove();
    map = null;
  }
  document.removeEventListener("click", handleFirstInteraction);
  document.removeEventListener("touchstart", handleFirstInteraction);
});
</script>

<style scoped>
.map-page {
  position: relative;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

.map-container {
  position: relative;
  height: calc(100vh - 120px);
  width: 100%;
  transition: all 0.3s ease;
  pointer-events: auto;
}

.map-container.fullscreen {
  height: 100vh;
}

#map {
  height: 100%;
  width: 100%;
  z-index: 0;
}

/* Map overlay containers */
.map-overlay {
  position: absolute;
  z-index: 1000;
  pointer-events: none;
}

.map-overlay > * {
  pointer-events: auto;
}

.map-overlay.top-left {
  top: 10px;
  left: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: calc(100vw - 20px);
}

.map-overlay.top-right {
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Control buttons */
.control-btn {
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  font-size: 16px;
  min-height: 44px;
  min-width: 44px;
}

.control-btn:hover {
  transform: translateY(-1px);
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Status Bar */
.status-bar {
  background: rgba(255, 255, 255, 0.95);
  padding: 12px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.status-info-group {
  display: flex;
  gap: 15px;
  align-items: center;
}

.status-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.status-label {
  font-size: 0.8rem;
  color: #666;
  font-weight: 600;
}

.status-value {
  font-size: 1rem;
  font-weight: 700;
  color: #222;
}

.user-status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
}

.user-status-badge.free {
  background: rgba(52, 168, 83, 0.95);
  color: white;
}

.user-status-badge.busy,
.user-status-badge.picking_up,
.user-status-badge.delivering {
  background: rgba(234, 67, 53, 0.95);
  color: white;
}

/* ✅ Order Detail Button */
.order-detail-button-container {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  width: 90%;
  max-width: 350px;
}

.order-detail-btn {
  width: 100%;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid #4285f4;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.order-detail-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.order-detail-btn .btn-icon {
  font-size: 20px;
  margin-right: 10px;
}

.order-detail-btn .btn-text {
  font-weight: 600;
  color: #333;
  flex: 1;
  text-align: left;
  font-size: 14px;
}

.order-detail-btn .btn-arrow {
  color: #4285f4;
  font-size: 14px;
}

/* ✅ Order Detail Modal */
.order-detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.order-detail-content {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  padding: 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
  border-radius: 16px 16px 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-modal-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0 8px;
  transition: color 0.2s;
}

.close-modal-btn:hover {
  color: #333;
}

/* Modal Sections */
.order-info-section,
.shop-section,
.customer-section,
.items-section {
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
}

.info-label {
  color: #666;
  font-weight: 500;
}

.info-value {
  color: #333;
  font-weight: 600;
}

.info-value.status-badge {
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  text-transform: uppercase;
}

.info-value.status-badge.accepted {
  background: #e3f2fd;
  color: #1976d2;
}

.info-value.status-badge.onway {
  background: #fff3e0;
  color: #e65100;
}

.info-value.status-badge.picked_up {
  background: #e8f5e9;
  color: #2e7d32;
}

.info-value.status-badge.completed {
  background: #f1f8e9;
  color: #33691e;
}

.info-value.status-badge.pending {
  background: #fff8e1;
  color: #f57f17;
}

/* Shop & Customer Info */
.shop-section h4,
.customer-section h4,
.items-section h4 {
  margin: 0 0 10px 0;
  font-size: 15px;
  color: #555;
}

.shop-info,
.customer-info {
  padding: 8px 0;
}

.shop-name,
.customer-name {
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.shop-phone,
.customer-phone,
.shop-address,
.customer-address {
  font-size: 13px;
  color: #666;
  margin-top: 2px;
}

/* ✅ Location Buttons */
.location-btn-section {
  padding: 12px 20px;
  display: flex;
  gap: 10px;
  border-bottom: 1px solid #f5f5f5;
}

.location-btn {
  flex: 1;
  padding: 10px 16px;
  border-radius: 10px;
  border: none;
  background: #ff6b00;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 13px;
}

.location-btn.shop {
  background: #2c3e50;
}

.location-btn:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

/* Items */
.item-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 14px;
}

.item-row:last-child {
  border-bottom: none;
}

.item-info {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.item-name {
  font-weight: 500;
  color: #333;
}

.item-details {
  font-size: 12px;
  color: #888;
}

.item-total {
  font-weight: 600;
  color: #333;
  align-self: center;
}

.items-total {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
  border-top: 1px solid #eee;
}

.items-total.grand-total {
  font-weight: 700;
  font-size: 16px;
  border-top: 2px solid #4285f4;
  padding-top: 12px;
  margin-top: 4px;
}

.total-label {
  color: #666;
}

.total-value {
  color: #333;
}

/* Modal Footer */
.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #eee;
  text-align: center;
  background: #f8f9fa;
  border-radius: 0 0 16px 16px;
}

.modal-footer .close-btn {
  padding: 10px 30px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.modal-footer .close-btn:hover {
  background: #3367d6;
}

/* Loading & Error States */
.loading-state,
.error-state {
  padding: 30px 20px;
  text-align: center;
  color: #666;
}

.error-state {
  color: #d32f2f;
}

.retry-btn {
  margin-top: 10px;
  padding: 6px 16px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

/* Action Buttons */
.action-button-container {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  width: 90%;
  max-width: 300px;
}

.action-button {
  width: 100%;
  padding: 16px 24px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.received-button {
  background-color: #ff6b00;
  color: white;
}

.received-button:hover {
  background-color: #e55a00;
  transform: translateY(-2px);
}

.complete-button {
  background-color: #34a853;
  color: white;
}

.complete-button:hover {
  background-color: #2e8b47;
  transform: translateY(-2px);
}

/* Modals */
.order-alert-modal,
.order-taken-modal,
.order-complete-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.alert-content,
.taken-content,
.complete-modal-content {
  background: white;
  padding: 30px;
  border-radius: 16px;
  max-width: 450px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: alert-appear 0.3s ease-out;
}

@keyframes alert-appear {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.alert-actions,
.complete-actions {
  margin-top: 20px;
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  min-width: 120px;
}

.btn-primary {
  background: #4285f4;
  color: white;
}

.btn-primary:hover {
  background: #3367d6;
  transform: translateY(-2px);
}

.btn-outline-secondary {
  background: transparent;
  color: #666;
  border: 2px solid #ddd;
}

.btn-outline-secondary:hover {
  border-color: #999;
  background: #f8f9fa;
}

/* Interaction prompt */
.interaction-prompt {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.95);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 3000;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 2px solid #4285f4;
  animation: pulse 2s infinite;
  cursor: pointer;
  pointer-events: auto;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.prompt-content {
  pointer-events: none;
}

.prompt-content p {
  margin: 0 0 8px 0;
  font-weight: 600;
  color: #333;
}

.prompt-content small {
  color: #666;
  font-size: 0.9em;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.02);
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
}

/* Order summary */
.order-summary {
  margin: 20px 0;
  text-align: left;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 15px;
}

.shop-group {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.shop-group:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.shop-name {
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #f5f5f5;
}

.summary-item.total {
  font-weight: bold;
  border-bottom: none;
  border-top: 2px solid #eee;
  padding-top: 10px;
  margin-top: 15px;
}

.summary-label {
  color: #555;
}

.summary-value {
  font-weight: 600;
  color: #222;
}

/* Hide/show desktop/mobile components */
.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

@media (max-width: 767px) {
  .desktop-only {
    display: none !important;
  }
  .mobile-only {
    display: block;
  }
  .map-container {
    height: calc(100vh - 80px);
  }
  .order-detail-button-container {
    bottom: 70px;
  }
  .order-detail-btn .btn-text {
    font-size: 13px;
  }
  .order-detail-content {
    max-height: 90vh;
    margin: 10px;
  }
  .location-btn-section {
    flex-direction: column;
  }
  .location-btn {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .order-detail-button-container {
    bottom: 65px;
    max-width: 95%;
  }
  .order-detail-btn {
    padding: 10px 16px;
  }
  .order-detail-btn .btn-text {
    font-size: 12px;
  }
  .order-detail-content {
    max-width: 100%;
    border-radius: 12px;
  }
  .modal-header h3 {
    font-size: 16px;
  }
  .info-row {
    font-size: 13px;
  }
}

.complete-modal-content {
  background: white;
  padding: 30px;
  border-radius: 16px;
  max-width: 450px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: alert-appear 0.3s ease-out;
}

/* Summary Row */
.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 14px;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-label {
  color: #555;
  font-weight: 500;
}

.summary-value {
  color: #333;
  font-weight: 600;
}

/* Shop Group */
.shop-group {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  margin: 8px 0;
}

.shop-name {
  font-weight: 600;
  color: #333;
  font-size: 15px;
}

.shop-phone {
  font-size: 13px;
  color: #666;
  margin-top: 2px;
}

/* Customer Info */
.customer-info {
  background: #e3f2fd;
  padding: 12px;
  border-radius: 8px;
  margin: 8px 0;
}

.customer-name {
  font-weight: 600;
  color: #1976d2;
  font-size: 15px;
}

.customer-phone {
  font-size: 13px;
  color: #555;
  margin-top: 2px;
}

/* Items Summary */
.items-summary {
  margin: 12px 0;
}

.items-title {
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 14px;
}

.group-items {
  margin-bottom: 8px;
}

.item-row-mini {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 13px;
  border-bottom: 1px dashed #eee;
}

.item-row-mini:last-child {
  border-bottom: none;
}

.item-name-mini {
  flex: 1;
  color: #333;
}

.item-details-mini {
  color: #888;
  margin: 0 8px;
}

.item-total-mini {
  font-weight: 600;
  color: #333;
}

/* Total Summary */
.summary-item.total {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  margin-top: 8px;
  border-top: 2px solid #4285f4;
  font-size: 16px;
}

.summary-item.total .summary-label {
  font-weight: 700;
  color: #333;
}

.summary-item.total .summary-value {
  font-weight: 700;
  color: #4285f4;
  font-size: 18px;
}
</style>